import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AbilityService } from 'ability';
import { MbsSize, ModalService, ModalSettings } from 'mbs-ui-kit';
import { TwoFactorApproveModalComponent } from 'mbs-ui-shared@components';
import { ConfirmCodeResponse, QRCodeResponse, TFAAuthType, TFAData, TFAEnabledStatus, TFAStatus } from 'mbs-ui-shared@models';
import {
  AddReserveAuthRequest,
  AddReserveAuthResponse,
  ConfirmReserveAuthRequest,
  ConfirmReserveAuthResponse,
  QrCode,
  ReserveAuthSettings,
  SendReserveAuthRequest
} from 'mbs-ui-shared@models/tfa';
import { Observable, from, switchMap, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TFAService {
  private http = inject(HttpClient);
  private ability = inject(AbilityService);
  private modal = inject(ModalService);

  public doNotDisturb(authId: string, minutes: number) {
    return this.http.post('api/v2/tfa/do-not-disturb', {
      AuthId: authId,
      Minutes: minutes
    });
  }

  public tryAgain(authId: string): Observable<TFAData> {
    return this.http.post<TFAData>(`api/v2/tfa/retry/${authId}`, null);
  }

  public getGoogleQRCode(): Observable<QRCodeResponse> {
    return this.http.post<QRCodeResponse>('api/v2/tfa/time-based-one-time-code/turn-on', null).pipe(
      map((result) => {
        return {
          ...result,
          payload: `/api/v2/tfa/QR?payload=${result.payload}`
        };
      })
    );
  }

  public getTFAStatus(authId: string): Observable<TFAStatus> {
    return this.http.get<TFAStatus>(`api/v2/tfa/status?authId=${authId}`);
  }

  public confirmGoogle(code: string, authId: string): Observable<ConfirmCodeResponse> {
    return this.http.post<ConfirmCodeResponse>('api/v2/tfa/time-based-one-time-code/confirm', {
      code,
      authId
    });
  }

  public activateGoogle(code: string, authId: string): Observable<{ isValid: boolean }> {
    return this.http.post<{ isValid: boolean }>('api/v2/tfa/time-based-one-time-code/confirm', {
      code,
      authId
    });
  }

  public confirmAlternativeCode(code: string, authId: string): Observable<ConfirmCodeResponse> {
    // Every fifth character is a line (-)
    const preparedCode = code
      .replace(/[^0-9a-zA-Z]/g, '')
      .replace(/(.{4})(?!$)/g, '$1-')
      .slice(0, 14);

    return this.http.post<ConfirmCodeResponse>('api/v2/tfa/alternative-codes/confirm', {
      code: preparedCode,
      authid: authId
    });
  }

  public refuse() {
    return this.http.post('api/v2/tfa/refuse', null);
  }

  public administratorDisableTFA(id: string) {
    return this.http.delete(`api/administrators/${id}/two-factor-auth`);
  }

  public resetAuthId() {
    return this.http.post('api/v2/tfa/reset-auth-id', null);
  }

  public updateTFAState() {
    return this.getTFAEnabledStatus().pipe(
      tap((result: { enabled: boolean; hasToBeEnabled: boolean }) => {
        if (result.enabled) {
          this.ability.addRule({ actions: 'read', subject: '2FAEnabled' });
        }
        if (!result.enabled) {
          this.ability.deleteRule('2FAEnabled', 'read');
        }
      })
    );
  }

  public openApproveModal(data: ModalSettings, container?: string) {
    return this.modal
      .openCustom(TwoFactorApproveModalComponent, {
        data,
        size: MbsSize.sm,
        container
      })
      .catch((error) => {
        this.resetAuthId().subscribe();
        return Promise.reject(error);
      });
  }

  public handleTFA<T>(request: Observable<T>, container?: string): Observable<T> {
    const tfaStatusCode = 420;
    return request.pipe(
      catchError((error) => {
        if (error.status === tfaStatusCode) {
          return from(this.openApproveModal(error.error, container)).pipe(switchMap(() => this.handleTFA(request, container)));
        }

        throw error;
      })
    );
  }
  // TODO: Use this one in handle TFA
  public manualHandleTFA<T>(request: Observable<T>): Observable<unknown> {
    const tfaStatusCode = 420;
    return request.pipe(
      catchError((error) => {
        if (error.status === tfaStatusCode) {
          return from(this.openApproveModal(error.error));
        }

        throw error;
      })
    );
  }

  public getTFAEnabledStatus(): Observable<TFAEnabledStatus> {
    return this.http.get<TFAEnabledStatus>('api/v2/tfa/IsTwoFactorAuthEnabled');
  }

  public getAuthType(): Observable<TFAAuthType> {
    return this.http.get<TFAAuthType>('api/v2/tfa/auth-type');
  }

  public turnOffTFA() {
    return this.http.delete<{ redirectUrl: string }>('api/v2/tfa/turn-off');
  }

  public resetCodes(): Observable<Array<string>> {
    return this.http.post<Array<string>>('api/v2/tfa/alternative-codes/reset', null);
  }

  public generateCodes(authId: string): Observable<Array<string>> {
    return this.http.post<Array<string>>('api/v2/tfa/alternative-codes/generate-new', { authId });
  }

  public getCodesCount(): Observable<number> {
    return this.http.get<number>('api/v2/tfa/alternative-codes/count');
  }

  public isRefused() {
    return this.http.get<boolean>('api/v2/tfa/is-refused');
  }

  public getReserveAuthSettings(authId: string): Observable<ReserveAuthSettings> {
    return this.http.get<ReserveAuthSettings>('api/v2/tfa/alt-auth/settings', { params: { authId } });
  }

  public enableReserveAuthSettings(): Observable<ReserveAuthSettings> {
    return this.http.get<ReserveAuthSettings>('api/v2/tfa/alt-auth/access');
  }

  public disableReserveAuthSettings(payload?: { authId: string }): Observable<ReserveAuthSettings> {
    return this.http.post<ReserveAuthSettings>('api/v2/tfa/alt-auth/disable', payload);
  }

  public addNewEmail(payload: AddReserveAuthRequest): Observable<AddReserveAuthResponse> {
    return this.http.post<AddReserveAuthResponse>('api/v2/tfa/alt-auth/email/new', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public confirmNewEmail(payload: ConfirmReserveAuthRequest): Observable<ConfirmReserveAuthResponse> {
    return this.http.post<ConfirmReserveAuthResponse>('api/v2/tfa/alt-auth/email/new/confirm', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public addNewPhone(payload: AddReserveAuthRequest): Observable<AddReserveAuthResponse> {
    return this.http.post<AddReserveAuthResponse>('api/v2/tfa/alt-auth/phone/new', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public confirmNewPhone(payload: ConfirmReserveAuthRequest): Observable<ConfirmReserveAuthResponse> {
    return this.http.post<ConfirmReserveAuthResponse>('api/v2/tfa/alt-auth/phone/new/confirm', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public sendSMS(payload: SendReserveAuthRequest): Observable<AddReserveAuthResponse> {
    return this.http.post<AddReserveAuthResponse>('api/v2/tfa/alt-auth/sms/code', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public confirmSMS(payload: ConfirmReserveAuthRequest): Observable<ConfirmReserveAuthResponse> {
    return this.http.post<ConfirmReserveAuthResponse>('api/v2/tfa/alt-auth/sms/code/confirm', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public sendEmail(payload: SendReserveAuthRequest): Observable<AddReserveAuthResponse> {
    return this.http.post<AddReserveAuthResponse>('api/v2/tfa/alt-auth/email/code', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public confirmEmail(payload: ConfirmReserveAuthRequest): Observable<ConfirmReserveAuthResponse> {
    return this.http.post<ConfirmReserveAuthResponse>('api/v2/tfa/alt-auth/email/code/confirm', payload, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  public getQrCode(authId): Observable<QrCode> {
    return this.http.get<QrCode>('/api/v2/tfa/operation-payload', {
      params: {
        authId
      }
    });
  }
}
